import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Alert, Badge, Button, Card } from '@/components/ui'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import type { BankReconciliationTieOut } from '@/hooks/useBankReconciliation'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { formatBaht } from '@/lib/money'

interface BankStatementTieOutCardProps {
  tieOut: BankReconciliationTieOut
}

export function BankStatementTieOutCard({ tieOut }: BankStatementTieOutCardProps) {
  const { t } = useTranslation('bank_reconciliation')
  const { data: account } = useAccountSettings()
  const formatDate = useDateFormatter()
  if (account?.settings.accounting_enabled !== true) return null

  // Server ISO date-only values; display formatting never enters comparison logic.
  const openingAfterStatement = tieOut.opening_balance_posted && !!tieOut.opening_balance_as_of_date &&
    tieOut.opening_balance_as_of_date > tieOut.as_of_date
  const comparisonAvailable = tieOut.ledger_balance != null && tieOut.difference != null && tieOut.matched != null
  const canConfirm = comparisonAvailable && tieOut.opening_balance_posted && !openingAfterStatement

  return (
    <Card role="region" aria-label={t('tie_out.title')} bodyClassName="gap-4 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3 min-w-0">
        <div className="min-w-0 space-y-1">
          <h2 className="card-title">{t('tie_out.title')}</h2>
          <p className="font-medium break-words">
            {t('banks.' + tieOut.bank_name)} · {tieOut.bank_account_number
              ? t('tie_out.account_number', { number: tieOut.bank_account_number })
              : t('tie_out.account_unknown')}
          </p>
          <p className="text-sm">{t('tie_out.as_of', { date: formatDate(tieOut.as_of_date) })}</p>
        </div>
        {canConfirm && (
          <Badge variant={tieOut.matched ? 'success' : 'warning'}>
            {t(tieOut.matched ? 'tie_out.matched' : 'tie_out.different')}
          </Badge>
        )}
      </div>
      <p className="text-sm text-base-content/60">{t('tie_out.scope')}</p>
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="min-w-0">
          <dt className="text-sm text-base-content/60">{t('tie_out.ledger_balance')}</dt>
          <dd className="text-xl font-semibold tabular-nums break-all">{formatBaht(tieOut.ledger_balance)}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-sm text-base-content/60">{t('tie_out.statement_balance')}</dt>
          <dd className="text-xl font-semibold tabular-nums break-all">{formatBaht(tieOut.statement_balance)}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-sm text-base-content/60">{t('tie_out.difference')}</dt>
          <dd className={`text-xl font-semibold tabular-nums break-all ${tieOut.matched === false ? 'text-error' : ''}`}>
            {formatBaht(tieOut.difference)}
          </dd>
        </div>
      </dl>
      {!comparisonAvailable && <Alert variant="info" role="status">{t('tie_out.ledger_unavailable')}</Alert>}
      {!tieOut.opening_balance_posted && (
        <Alert variant="warning" role="status">
          <div className="flex flex-wrap items-center gap-3">
            <span>{t('tie_out.opening_missing')}</span>
            <Button as={Link} to="/accounting/chart-of-accounts?opening_balance=1" size="sm" variant="outline">
              {t('tie_out.set_opening')}
            </Button>
          </div>
        </Alert>
      )}
      {openingAfterStatement && (
        <Alert variant="warning" role="status">
          {t('tie_out.opening_after_statement', { date: formatDate(tieOut.opening_balance_as_of_date) })}
        </Alert>
      )}
    </Card>
  )
}
